import { ActionContext } from "vuex";
import { notification } from "ant-design-vue";
import { fmtMsg } from "vue-glcommonui";
import { NetworkQualityPayload } from "@/store/room/interface";
import { StudentRoomState } from "@/store/room/student/state";
import { StoreLocale } from "@/locales/localeid";
import { MainRoles, RemoteTeachingService } from "@/services";
import { CallingUserStatus } from "@/store/calling/mutations";
import { ConnectionDisconnectedReason, ConnectionState } from "agora-rtc-sdk-ng";
import router from "@/router";
import { Paths } from "@/utils/paths";
import { delay, isReconnectedSuccess, isSameUserJoined, needTriggerManualReconnect } from "@/utils/utils";
import { notifySameUserJoined } from "@/utils/notifications";
import { MediaUserPublishStatus } from "@/store/calling/utils";
import { vuexName, MODULES } from "@/store/utils";
import { NetworkHealthTracker } from "@/agora/networkHealthTracker";
import { DownlinkTracker } from "@/agora/downlinkTracker";

export const callingUtils = ({ commit, dispatch, state, rootState }: ActionContext<StudentRoomState, any>): any => {
  const studentTracker = new NetworkHealthTracker();
  const downlinkTracker = new DownlinkTracker();

  return {
    remoteUserJoined: async (id: string) => {
      await dispatch(vuexName(MODULES.STUDENT_ROOM.DISPATCHES.HANDLE_REMOTE_USER_JOINED, false), id);
    },
    remoteUserLeft: async (id: string) => {
      const { helper, teacher } = state;
      commit(vuexName(MODULES.CALLING.COMMITS.SET_CALLING_USER_IDS), { id, status: CallingUserStatus.LEFT }, { root: true });
      if (state.isSessionEnded || !state.info?.id || !state.student?.id) return;
      let remoteUserRole = MainRoles.TEACHER;
      if (helper?.id === id) {
        remoteUserRole = MainRoles.HELPER;
      } else if (teacher?.id === id) {
        commit(vuexName(MODULES.STUDENT_ROOM.COMMITS.SET_TEACHER_DISCONNECTED, false), true);
        notification.warn({
          message: fmtMsg(StoreLocale.WaitYourTeacher),
        });
      } else {
        remoteUserRole = MainRoles.STUDENT;
      }
      //** Informing to the SERVER that user is disconnected
      RemoteTeachingService.putRemoteUserDisconnected({
        remoteUserRole: remoteUserRole,
        remoteUserId: id,
        sessionId: state.info.id,
        localUserId: state.student.id,
      });
    },
    localUserConnectionChanged: async (curState: ConnectionState, prevState: ConnectionState, reason: ConnectionDisconnectedReason | undefined) => {
      if (isSameUserJoined(curState, reason)) {
        if (state.manager) {
          await state.manager.handleKickOut("agora");
        } else {
          notifySameUserJoined();
          await router.push(Paths.Parent);
        }
        return;
      }
      const sessionId = state.info?.id;
      const localStudentId = state.student?.id;
      if (!localStudentId || !sessionId) return;
      if (isReconnectedSuccess(curState, prevState)) {
        if (state.manager) {
          // If the user is reconnected, leave channel to rejoin later
          await state.manager.agoraClient?.leaveChannel();
        }
        return;
      }
      commit(
        vuexName(MODULES.CALLING.COMMITS.SET_CALLING_USER_IDS),
        { id: localStudentId, status: curState === "CONNECTED" ? CallingUserStatus.JOINED : CallingUserStatus.LEFT },
        { root: true },
      );
      await dispatch(
        `${
          curState === "CONNECTED" || curState === "CONNECTING"
            ? vuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_ONLINE, false)
            : vuexName(MODULES.STUDENT_ROOM.DISPATCHES.SET_OFFLINE, false)
        }`,
      );
      curState === "CONNECTED" &&
        (await RemoteTeachingService.postLocalUserReconnected({
          localUserRole: MainRoles.STUDENT,
          localUserId: localStudentId,
          sessionId,
        }));
      // Manually reconnecting the Agora channel to secure everything is up-to-date
      if (needTriggerManualReconnect(curState)) {
        if (state.manager) {
          if (state.manager.agoraClient.client.channelName) {
            // if the local user is in the channel, leave the channel and rejoin
            await state.manager.agoraClient?.leaveChannel();
          }
          // delay 2s to make sure the user has left the channel
          await delay(2000);
          // rejoin the channel to up-to-date the remote users
          await state.manager.manualReconnectAgoraRoom();
        }
      }
    },
    handleException: async () => {
      //
    },
    bandwidthUpdateBasedOnAgora: async (payload: NetworkQualityPayload) => {
      const { uplinkNetworkQuality, downlinkNetworkQuality } = payload;

      const isWeak = studentTracker.update(uplinkNetworkQuality, downlinkNetworkQuality);

      // Update downlink tracker with downlink quality only
      const isWeakDueToDownlink = downlinkTracker.update(downlinkNetworkQuality);

      if (isWeak !== rootState.app.isLowBandWidth) {
        await dispatch(vuexName(MODULES.APP.DISPATCHES.SET_LOW_BAND_WIDTH, false), isWeak, { root: true });
      }

      if (isWeakDueToDownlink !== rootState.app.isLowBandWidthDueToDownlink) {
        await dispatch("setLowBandWidthDueToDownlink", isWeakDueToDownlink, { root: true });
      }
    },
    handleUserPublishedVideo: async ({ id, status }: { id: string; status: MediaUserPublishStatus }) => {
      commit(vuexName(MODULES.CALLING.COMMITS.SET_VIDEO_USER_IDS), { id, status }, { root: true });
    },
    handleUserPublishedAudio: async ({ id, status }: { id: string; status: MediaUserPublishStatus }) => {
      commit(vuexName(MODULES.CALLING.COMMITS.SET_AUDIO_USER_IDS), { id, status }, { root: true });
    },
  };
};
